const API_URL = import.meta.env.VITE_API_URL ?? "/api";

export const AUTH_TOKEN_KEY = "auth:token";

/**
 * HTTP error from the API. `message` stays `API error: <status>` (callers
 * string-match it, e.g. AuthContext checks for "401"); the backend's `detail`
 * string, when present, is exposed separately.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail?: string;

  constructor(status: number, detail?: string) {
    super(`API error: ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

interface ApiFetchOptions extends RequestInit {
  responseType?: "json" | "blob" | "text";
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { responseType, ...fetchOptions } = options;
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const headers: Record<string, string> = {
    ...((fetchOptions.headers as Record<string, string>) ?? {}),
  };

  // Only set Content-Type for non-FormData bodies — the browser sets the
  // correct multipart boundary automatically for FormData.
  if (!(fetchOptions.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...fetchOptions,
      headers,
    });
  } catch {
    // Network error (cold start, connection reset, etc.) — throw a
    // distinguishable error so callers can retry instead of logging out.
    throw new Error("Network error");
  }

  if (!response.ok) {
    // Exempt /auth/login so a wrong-password 401 doesn't replace the inline
    // error with a blank login redirect.
    if (response.status === 401 && path !== "/auth/login") {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      window.location.href = "/login";
    }
    let detail: string | undefined;
    try {
      const body = await response.json();
      // Server errors use the ErrorResponse envelope ({error, code}); plain
      // FastAPI responses use {detail}. Accept either.
      if (typeof body?.error === "string") detail = body.error;
      else if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      // non-JSON error body — keep the status-only error
    }
    throw new ApiError(response.status, detail);
  }

  // Handle 204 No Content — return undefined without calling response.json()
  if (response.status === 204) {
    return undefined as T;
  }

  // Return a Blob when explicitly requested. When the server names the file
  // (Content-Disposition), return a File carrying that name so downloads can
  // keep it (e.g. the informe is named after the drill).
  if (responseType === "blob") {
    const blob = await response.blob();
    const name = filenameFromContentDisposition(response.headers.get("Content-Disposition"));
    return (name ? new File([blob], name, { type: blob.type }) : blob) as T;
  }

  // Return plain text when explicitly requested (e.g. HTML responses)
  if (responseType === "text") {
    return response.text() as Promise<T>;
  }

  return response.json() as Promise<T>;
}

/** File name from a Content-Disposition header: RFC 5987 `filename*` first, then `filename`. */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // malformed encoding: fall through to the plain form
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
  return plain?.[1] ? plain[1].trim() : null;
}
